import { toast } from 'react-hot-toast';

import { FieldValuePure } from '../FieldValue';
import { CompoundInputPure } from './CompoundInputPure';

export type OrgFieldName = 'name' | 'nameRaw' | 'orgId' | 'pictureUrl';
export type OrgField = { primary: boolean; source: string; data: string };

type CompoundOrgInputProps = {
  title?: string;
  placeholder: string;
  name: OrgFieldName;
  expanded?: boolean;
  setExpanded?: (name: OrgFieldName, expanded: boolean) => void;
  fields?: OrgField[];
  disabled?: boolean;
  value?: string;
  onAdd?: (name: OrgFieldName, value: string, source: string) => void;
  onMakePrimary?: (name: OrgFieldName, source: string) => void;
  onRemovePrimary?: (name: OrgFieldName, source: string) => void;
  onDeleteValue?: (name: OrgFieldName, source: string) => void;
};

export function CompoundOrgInput({
  title,
  placeholder,
  name,
  expanded,
  setExpanded,
  fields,
  disabled,
  value,
  onAdd,
  onMakePrimary,
  onRemovePrimary,
  onDeleteValue,
}: CompoundOrgInputProps) {
  const hasPrimaryValue = fields?.some((f) => f.primary) ?? false;
  const showChevron = (fields?.length ?? 0) > 1;

  const canDeleteSource = (source: string) =>
    source?.startsWith('input') ||
    source?.startsWith('crm') ||
    source?.startsWith('csv') ||
    source?.startsWith('close') ||
    source?.startsWith('ai');

  return (
    <CompoundInputPure
      title={title}
      placeholder={placeholder}
      expanded={expanded}
      onExpandedChange={setExpanded ? (exp) => setExpanded(name, exp) : undefined}
      hasValues={(fields?.length ?? 0) > 0}
      hasPrimaryValue={hasPrimaryValue}
      showChevron={showChevron}
      disabled={disabled}
      disabledValue={value}
      onAdd={onAdd ? (val, src) => onAdd(name, val, src) : undefined}>
      {fields?.map((field, index) => {
        const isFieldPrimary = field.primary;
        if (!expanded && !isFieldPrimary) return null;

        return (
          <FieldValuePure
            key={String(index) + field.source + String(field.data)}
            data={String(field.data)}
            source={field.source}
            isPrimary={isFieldPrimary}
            onCopied={(d) => toast.success(`${d} copied to clipboard`)}
            onCopyFailed={() => toast.error('Failed to copy')}
            onMakePrimary={!isFieldPrimary && onMakePrimary ? () => onMakePrimary(name, field.source) : undefined}
            onRemovePrimary={isFieldPrimary && onRemovePrimary ? () => onRemovePrimary(name, field.source) : undefined}
            onDelete={canDeleteSource(field.source) && onDeleteValue ? () => onDeleteValue(name, field.source) : undefined}
          />
        );
      })}
    </CompoundInputPure>
  );
}
